import React, { useState, useEffect, useRef } from 'react';
import { Search, Building2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface Client {
  id: string;
  name: string;
  company_name: string;
}

interface ClientSearchBarProps {
  clients: Client[];
  selectedClientId: string;
  onClientSelect: (clientId: string) => void;
  disabled?: boolean;
  label?: string;
  required?: boolean;
}

const ClientSearchBar: React.FC<ClientSearchBarProps> = ({
  clients,
  selectedClientId,
  onClientSelect,
  disabled = false,
  label = 'Client',
  required = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedClient = clients.find(c => c.id === selectedClientId);

  const filteredClients = clients.filter(client => {
    const search = searchTerm.toLowerCase();
    return (
      client.name.toLowerCase().includes(search) ||
      (client.company_name?.toLowerCase().includes(search) ?? false)
    );
  }).slice(0, 5);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev =>
          prev < filteredClients.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && filteredClients[focusedIndex]) {
          handleSelectClient(filteredClients[focusedIndex].id);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleSelectClient = (clientId: string) => {
    onClientSelect(clientId);
    setIsOpen(false);
    setSearchTerm('');
    setFocusedIndex(-1);
  };

  const handleClear = () => {
    onClientSelect('');
    setSearchTerm('');
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label} {required && <span className="text-red-400">*</span>}
        <span className="text-xs text-gray-500 ml-2">(⌘K ou Ctrl+K)</span>
      </label>

      {selectedClient && !isOpen ? (
        <div className="flex items-center space-x-2 w-full px-4 py-3 bg-gray-700 border-2 border-blue-500 rounded-lg">
          <Building2 className="h-5 w-5 text-blue-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{selectedClient.name}</p>
            <p className="text-xs text-gray-400 truncate">{selectedClient.company_name}</p>
          </div>
          {!disabled && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-600 rounded transition-colors flex-shrink-0"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder="Rechercher un client..."
              disabled={disabled}
              className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <AnimatePresence>
            {isOpen && filteredClients.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute z-50 w-full mt-2 bg-gray-700 border border-gray-600 rounded-lg shadow-2xl max-h-80 overflow-y-auto"
              >
                {filteredClients.map((client, index) => (
                  <button
                    key={client.id}
                    onClick={() => handleSelectClient(client.id)}
                    className={`
                      w-full text-left px-4 py-3 transition-colors border-b border-gray-600 last:border-b-0
                      ${index === focusedIndex ? 'bg-blue-600' : 'hover:bg-gray-600'}
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-5 w-5 text-blue-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{client.name}</p>
                        <p className="text-xs text-gray-400 truncate">{client.company_name}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
};

export default ClientSearchBar;
